import * as React from 'react';

import { OpenDialog } from '~teacher/components/quizzes/CopyMoveDialog';

import { uniqueIdFactory } from '../../../../../api/quiz/getQuizzes/normalize/uniqueIds';

interface LibraryQuizActions {
  deleteQuiz: (uniqueId: string) => void;
  openMoveDialog: OpenDialog;
  openCopyDialog: OpenDialog;
}

const LibraryQuizActionContext =
  React.createContext<LibraryQuizActions>(undefined);

export interface LibraryQuizActionProviderProps {
  children: React.ReactNode;
  onDeleteQuiz: (quizId: number) => void;
  openMoveDialog: OpenDialog;
  openCopyDialog: OpenDialog;
}

/**
 * This context provides folder actions such as rename/move/etc to prevent prop drilling
 */
export function LibraryQuizActionProvider({
  children,
  onDeleteQuiz,
  openMoveDialog,
  openCopyDialog,
}: LibraryQuizActionProviderProps) {
  const quizActions: LibraryQuizActions = React.useMemo(
    () => ({
      deleteQuiz: (uniqueId: string) => {
        onDeleteQuiz(uniqueIdFactory.quiz.toEntityId(uniqueId));
      },
      openMoveDialog: openMoveDialog,
      openCopyDialog: openCopyDialog,
    }),
    [onDeleteQuiz, openMoveDialog, openCopyDialog]
  );

  return (
    <LibraryQuizActionContext.Provider value={quizActions}>
      {children}
    </LibraryQuizActionContext.Provider>
  );
}

export function useLibraryQuizActions() {
  const context = React.useContext(LibraryQuizActionContext);

  if (context === undefined) {
    throw new Error(
      'useLibraryQuizActions must be used within LibraryQuizActionContext'
    );
  }

  return context;
}
